import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {getUserExperience} from "../../service/userService";
import BasicGrid from "../grid/BasicGrid";
import {Button, Divider, Stack, toaster} from "rsuite";
import SuccessNotification from "../notifications/SuccessNotification";
import {
    EXPERIENCE_ADD_ERROR,
    EXPERIENCE_ADD_SUCCESS,
    EXPERIENCE_EDIT_ERROR,
    EXPERIENCE_EDIT_SUCCESS,
} from "../../service/constants";
import ErrorNotification from "../notifications/ErrorNotification";
import {parseISO} from "rsuite/cjs/utils/dateUtils";
import AddExperienceModal from "../modals/experience/AddExperienceModal";
import EditExperienceModal from "../modals/experience/EditExperienceModal";

// isPresent is in the data, but is only used by the grid for rendering
const defaultColumns = [
    {
        key: 'position',
        label: 'Position',
        flexGrow: 2,
    },
    {
        key: 'company',
        label: 'Company',
        flexGrow: 2,
    },
    {
        key: 'startDate',
        label: 'Date Start',
    },
    {
        key: 'endDate',
        label: 'Date End',
    },
];

const Experience = () => {

    // state
    const userInfo = useSelector((state) => state.user.userInfo );
    const [loading, setLoading] = useState(false);
    const [userExperience, setUserExperience] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedExp, setSelectedExp] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    const loadUserExperience = useCallback(() => {
        setLoading(true);
        getUserExperience(userInfo.id).then((experience) => {
            experience.forEach((exp) => {
                exp.startDate = parseISO(exp.startDate);
                exp.endDate = parseISO(exp.endDate);
            });
            experience.sort((a, b) => (a.endDate < b.endDate) ? 1 : -1);
            setUserExperience(experience);
            setLoading(false);
        });
    }, [userInfo.id]);

    useEffect(() => loadUserExperience(), [loadUserExperience]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EXPERIENCE_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUserExperience();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EXPERIENCE_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUserExperience]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EXPERIENCE_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUserExperience();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EXPERIENCE_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [loadUserExperience]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedExperience = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedExp(rowData);
    },[]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <h2>Experience</h2>
                <Stack spacing={20} direction='column' alignItems='flex-start'>
                    <p>
                        Click a row to edit/delete. See the projects section to associate projects with experience.
                    </p>
                    <p style={{'paddingBottom': '0.5rem'}}>
                        <b>Deleting experience will delete all associated projects</b>
                    </p>
                </Stack>
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Experience</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userExperience}
                    handleClick={setClickedExperience}
                    exportTitle={`${userInfo.lastName}-${userInfo.firstName}-experience`}
                />
                <AddExperienceModal
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    userId={userInfo.id}
                />
                <EditExperienceModal
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    experience={selectedExp}
                    userId={userInfo.id}
                />
            </div>
        );
    }, [
        addModalOpen,
        columns,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedExp,
        setClickedExperience,
        userExperience,
        userInfo.id,
        userInfo.firstName,
        userInfo.lastName,
    ]);
}

export default Experience;